import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useLeague } from '@/contexts/LeagueContext';
import { useAuth } from '@/contexts/AuthContext';
import { useCommissionerActAs } from '@/contexts/CommissionerActAsContext';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  ShieldAlert,
  UserPlus,
  UserMinus,
  ArrowLeftRight,
  BarChart3,
  Users,
  Search,
  Undo2,
  AlertTriangle,
  CheckCircle2,
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface TeamOption {
  id: string;
  name: string;
}

interface PlayerSearchResult {
  id: string;
  full_name: string;
  position_abbreviation: string;
  mlb_team_abbreviation: string | null;
}

interface RosteredPlayer {
  player_id: string;
  full_name: string;
  position_abbreviation: string;
  slot: string;
}

interface CompletedTrade {
  id: string;
  proposer_team_name: string;
  receiver_team_name: string;
  created_at: string;
  player_names: string[];
}

interface MatchupOption {
  id: string;
  week: number;
  home_team: string;
  away_team: string;
  home_score: number;
  away_score: number;
  home_team_id: string;
}

const CommissionerOverrides = ({ leagueId }: { leagueId: string }) => {
  const { teams } = useLeague();
  const { user } = useAuth();
  const { toast } = useToast();
  const { actAsTeamId, actAsTeamName, isActingAs, setActAsTeam, exitMode } = useCommissionerActAs();

  const [processing, setProcessing] = useState(false);
  const [lastResult, setLastResult] = useState<{ action: string; message: string } | null>(null);
  const [actAsPickerId, setActAsPickerId] = useState('');

  // Force Add state
  const [addTeamId, setAddTeamId] = useState('');
  const [addSearch, setAddSearch] = useState('');
  const [addResults, setAddResults] = useState<PlayerSearchResult[]>([]);
  const [addSearching, setAddSearching] = useState(false);
  const [selectedAddPlayer, setSelectedAddPlayer] = useState<PlayerSearchResult | null>(null);

  // Force Drop state
  const [dropTeamId, setDropTeamId] = useState('');
  const [dropRoster, setDropRoster] = useState<RosteredPlayer[]>([]);
  const [dropLoading, setDropLoading] = useState(false);
  const [selectedDropPlayer, setSelectedDropPlayer] = useState<RosteredPlayer | null>(null);

  // Reverse Trade state
  const [completedTrades, setCompletedTrades] = useState<CompletedTrade[]>([]);
  const [tradesLoading, setTradesLoading] = useState(false);
  const [confirmReverse, setConfirmReverse] = useState<string | null>(null);

  // Adjust Score state
  const [matchups, setMatchups] = useState<MatchupOption[]>([]);
  const [matchupsLoading, setMatchupsLoading] = useState(false);
  const [selectedMatchup, setSelectedMatchup] = useState<MatchupOption | null>(null);
  const [newHomeScore, setNewHomeScore] = useState('');
  const [newAwayScore, setNewAwayScore] = useState('');

  // Set Lineup state
  const [lineupTeamId, setLineupTeamId] = useState('');
  const [lineupRoster, setLineupRoster] = useState<RosteredPlayer[]>([]);
  const [lineupLoading, setLineupLoading] = useState(false);
  const [lineupSwapSource, setLineupSwapSource] = useState<RosteredPlayer | null>(null);

  const teamOptions: TeamOption[] = teams.map(t => ({ id: t.id, name: t.name }));

  const callOverride = async (payload: any) => {
    setProcessing(true);
    setLastResult(null);
    try {
      const { data: session } = await supabase.auth.getSession();
      const resp = await supabase.functions.invoke('commissioner-override', {
        body: { ...payload, league_id: leagueId },
      });

      if (resp.error) throw new Error(resp.error.message);
      const result = resp.data;
      if (result.error) throw new Error(result.error);

      setLastResult({
        action: result.action,
        message: result.player
          ? `${result.action === 'force_add' ? 'Added' : 'Dropped'} ${result.player}`
          : result.players_returned
          ? `Reversed trade, ${result.players_returned} players returned`
          : result.moves
          ? `Set ${result.moves} lineup moves`
          : `Score adjusted`,
      });
      toast({ title: 'Override applied', description: 'Action completed successfully.' });
      return true;
    } catch (err: any) {
      toast({ title: 'Override failed', description: err.message, variant: 'destructive' });
      return false;
    } finally {
      setProcessing(false);
    }
  };

  // Search players for force-add
  const searchPlayers = async (query: string) => {
    if (!query.trim()) { setAddResults([]); return; }
    setAddSearching(true);
    const { data } = await supabase
      .from('players')
      .select('id, full_name, position_abbreviation, mlb_team_abbreviation')
      .eq('sport', 'mlb')
      .ilike('full_name', `%${query.trim()}%`)
      .limit(20);
    setAddResults(data || []);
    setAddSearching(false);
  };

  // Load roster for force-drop
  const loadRoster = async (teamId: string) => {
    if (!teamId) { setDropRoster([]); return; }
    setDropLoading(true);
    const { data } = await supabase
      .from('roster_assignments')
      .select('player_id, slot, players!inner(full_name, position_abbreviation)')
      .eq('team_id', teamId);

    setDropRoster(
      (data || []).map((r: any) => ({
        player_id: r.player_id,
        full_name: r.players.full_name,
        position_abbreviation: r.players.position_abbreviation,
        slot: r.slot,
      }))
    );
    setDropLoading(false);
  };

  // Load completed trades
  const loadTrades = async () => {
    setTradesLoading(true);
    const { data: tradesData } = await supabase
      .from('trades')
      .select('id, proposer_team_id, receiver_team_id, status, created_at')
      .eq('league_id', leagueId)
      .eq('status', 'completed')
      .order('created_at', { ascending: false })
      .limit(20);

    if (!tradesData || tradesData.length === 0) {
      setCompletedTrades([]);
      setTradesLoading(false);
      return;
    }

    const tradeIds = tradesData.map(t => t.id);
    const { data: tradePlayers } = await supabase
      .from('trade_players')
      .select('trade_id, players!inner(full_name)')
      .in('trade_id', tradeIds);

    const mapped = tradesData.map(t => {
      const proposer = teams.find(tm => tm.id === t.proposer_team_id);
      const receiver = teams.find(tm => tm.id === t.receiver_team_id);
      const players = (tradePlayers || [])
        .filter((tp: any) => tp.trade_id === t.id)
        .map((tp: any) => tp.players.full_name);

      return {
        id: t.id,
        proposer_team_name: proposer?.name || 'Unknown',
        receiver_team_name: receiver?.name || 'Unknown',
        created_at: t.created_at,
        player_names: players,
      };
    });

    setCompletedTrades(mapped);
    setTradesLoading(false);
  };

  // Load matchups
  const loadMatchups = async () => {
    setMatchupsLoading(true);
    const { data } = await supabase
      .from('matchups')
      .select('id, week, home_team_name, away_team_name, home_score, away_score, home_team_id')
      .eq('league_id', leagueId)
      .order('week', { ascending: false })
      .limit(50);

    setMatchups(
      (data || []).map(m => ({
        id: m.id,
        week: m.week,
        home_team: m.home_team_name,
        away_team: m.away_team_name,
        home_score: Number(m.home_score),
        away_score: Number(m.away_score),
        home_team_id: m.home_team_id,
      }))
    );
    setMatchupsLoading(false);
  };

  // Load lineup roster
  const loadLineupRoster = async (teamId: string) => {
    if (!teamId) { setLineupRoster([]); return; }
    setLineupLoading(true);
    const { data } = await supabase
      .from('roster_assignments')
      .select('player_id, slot, is_starter, players!inner(full_name, position_abbreviation)')
      .eq('team_id', teamId)
      .order('is_starter', { ascending: false });

    setLineupRoster(
      (data || []).map((r: any) => ({
        player_id: r.player_id,
        full_name: r.players.full_name,
        position_abbreviation: r.players.position_abbreviation,
        slot: r.slot,
        is_starter: r.is_starter,
      }))
    );
    setLineupLoading(false);
  };

  const handleForceAdd = async () => {
    if (!addTeamId || !selectedAddPlayer) return;
    const ok = await callOverride({
      action: 'force_add',
      team_id: addTeamId,
      player_id: selectedAddPlayer.id,
    });
    if (ok) {
      setSelectedAddPlayer(null);
      setAddSearch('');
      setAddResults([]);
    }
  };

  const handleForceDrop = async () => {
    if (!dropTeamId || !selectedDropPlayer) return;
    const ok = await callOverride({
      action: 'force_drop',
      team_id: dropTeamId,
      player_id: selectedDropPlayer.player_id,
    });
    if (ok) {
      setSelectedDropPlayer(null);
      loadRoster(dropTeamId);
    }
  };

  const handleReverseTrade = async (tradeId: string) => {
    const ok = await callOverride({ action: 'reverse_trade', trade_id: tradeId });
    if (ok) {
      setConfirmReverse(null);
      loadTrades();
    }
  };

  const handleAdjustScore = async () => {
    if (!selectedMatchup) return;
    const ok = await callOverride({
      action: 'adjust_score',
      matchup_id: selectedMatchup.id,
      home_score: newHomeScore,
      away_score: newAwayScore,
      team_id: selectedMatchup.home_team_id,
    });
    if (ok) {
      setSelectedMatchup(null);
      loadMatchups();
    }
  };

  const handleSetLineup = async (
    source: RosteredPlayer,
    target: RosteredPlayer
  ) => {
    if (!lineupTeamId) return;
    const ok = await callOverride({
      action: 'set_lineup',
      team_id: lineupTeamId,
      moves: [
        { player_id: source.player_id, slot: target.slot, is_starter: (target as any).is_starter ?? false },
        { player_id: target.player_id, slot: source.slot, is_starter: (source as any).is_starter ?? false },
      ],
    });
    if (ok) {
      setLineupSwapSource(null);
      loadLineupRoster(lineupTeamId);
    }
  };

  return (
    <div className="space-y-4">
      {/* Header warning */}
      <Card className="bg-destructive/5 border-destructive/30">
        <CardContent className="p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-sm text-destructive">Commissioner Overrides</p>
            <p className="text-xs text-muted-foreground mt-1">
              These actions bypass normal transaction rules. All overrides are logged in the league activity feed for transparency. Affected team owners receive a push notification on every action.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Manage another team's roster — SAB-341 act-as mode */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <ShieldAlert className="w-4 h-4 text-amber-500" />
            Manage Another Team's Roster
          </CardTitle>
          <CardDescription className="text-xs">
            Enter "acting on behalf of" mode for a specific team. Standard roster controls (drop, add, lineup, IL, waiver, trade-cancel) will apply to that team while a banner stays visible. Push + activity feed log every action with your name.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {isActingAs ? (
            <div className="flex items-center justify-between gap-3 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2">
              <div className="text-xs">
                Currently acting on behalf of <span className="font-semibold">{actAsTeamName}</span>.
              </div>
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={exitMode}>
                Exit mode
              </Button>
            </div>
          ) : (
            <>
              <div className="space-y-1">
                <Label className="text-xs">Team</Label>
                <Select value={actAsPickerId} onValueChange={setActAsPickerId}>
                  <SelectTrigger><SelectValue placeholder="Select team..." /></SelectTrigger>
                  <SelectContent>
                    {teamOptions.map(t => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                size="sm"
                disabled={!actAsPickerId}
                onClick={() => {
                  const t = teamOptions.find(x => x.id === actAsPickerId);
                  if (!t) return;
                  setActAsTeam({ teamId: t.id, teamName: t.name, leagueId });
                  setActAsPickerId('');
                  toast({ title: 'Commissioner mode active', description: `Acting on behalf of ${t.name}.` });
                }}
                className="w-full"
              >
                Enter mode
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      {lastResult && (
        <Card className="bg-primary/5 border-primary/30">
          <CardContent className="p-3 flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium">{lastResult.message}</span>
          </CardContent>
        </Card>
      )}

      {/* 1. FORCE ADD */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <UserPlus className="w-4 h-4 text-primary" />
            Force Add Player
          </CardTitle>
          <CardDescription className="text-xs">Add any player to any team's roster, bypassing waivers.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1">
            <Label className="text-xs">Target Team</Label>
            <Select value={addTeamId} onValueChange={setAddTeamId}>
              <SelectTrigger><SelectValue placeholder="Select team..." /></SelectTrigger>
              <SelectContent>
                {teamOptions.map(t => (
                  <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Search Player</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Player name..."
                value={addSearch}
                onChange={(e) => { setAddSearch(e.target.value); searchPlayers(e.target.value); }}
                className="pl-9"
              />
            </div>
          </div>

          {addResults.length > 0 && (
            <div className="max-h-40 overflow-y-auto space-y-1 rounded-md border border-border p-1">
              {addResults.map(p => (
                <button
                  key={p.id}
                  onClick={() => { setSelectedAddPlayer(p); setAddResults([]); setAddSearch(p.full_name); }}
                  className={cn(
                    "w-full text-left px-3 py-2 rounded text-sm hover:bg-muted/50 flex items-center justify-between",
                    selectedAddPlayer?.id === p.id && "bg-primary/10"
                  )}
                >
                  <span>{p.full_name}</span>
                  <Badge variant="outline" className="text-[10px]">{p.position_abbreviation} • {p.mlb_team_abbreviation || 'FA'}</Badge>
                </button>
              ))}
            </div>
          )}

          <Button
            onClick={handleForceAdd}
            disabled={!addTeamId || !selectedAddPlayer || processing}
            className="w-full gap-2"
            size="sm"
          >
            <UserPlus className="w-3.5 h-3.5" />
            {processing ? 'Adding...' : 'Force Add to Roster'}
          </Button>
        </CardContent>
      </Card>

      {/* 2. FORCE DROP */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <UserMinus className="w-4 h-4 text-destructive" />
            Force Drop Player
          </CardTitle>
          <CardDescription className="text-xs">Remove a player from any team's roster.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1">
            <Label className="text-xs">Team</Label>
            <Select
              value={dropTeamId}
              onValueChange={(v) => { setDropTeamId(v); setSelectedDropPlayer(null); loadRoster(v); }}
            >
              <SelectTrigger><SelectValue placeholder="Select team..." /></SelectTrigger>
              <SelectContent>
                {teamOptions.map(t => (
                  <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {dropLoading ? (
            <Skeleton className="h-20" />
          ) : dropRoster.length > 0 ? (
            <div className="max-h-48 overflow-y-auto space-y-1 rounded-md border border-border p-1">
              {dropRoster.map(p => (
                <button
                  key={p.player_id}
                  onClick={() => setSelectedDropPlayer(p)}
                  className={cn(
                    "w-full text-left px-3 py-2 rounded text-sm hover:bg-muted/50 flex items-center justify-between",
                    selectedDropPlayer?.player_id === p.player_id && "bg-destructive/10"
                  )}
                >
                  <span>{p.full_name}</span>
                  <Badge variant="outline" className="text-[10px]">{p.position_abbreviation} • {p.slot}</Badge>
                </button>
              ))}
            </div>
          ) : dropTeamId ? (
            <p className="text-xs text-muted-foreground text-center py-2">No players on roster</p>
          ) : null}

          <Button
            onClick={handleForceDrop}
            disabled={!dropTeamId || !selectedDropPlayer || processing}
            variant="destructive"
            className="w-full gap-2"
            size="sm"
          >
            <UserMinus className="w-3.5 h-3.5" />
            {processing ? 'Dropping...' : 'Force Drop Player'}
          </Button>
        </CardContent>
      </Card>

      {/* 3. REVERSE TRADE */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Undo2 className="w-4 h-4 text-accent-foreground" />
            Reverse Trade
          </CardTitle>
          <CardDescription className="text-xs">Undo a completed trade and return players to original teams.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Button
            variant="outline"
            size="sm"
            onClick={loadTrades}
            disabled={tradesLoading}
            className="w-full"
          >
            {tradesLoading ? 'Loading...' : 'Load Completed Trades'}
          </Button>

          {completedTrades.length > 0 && (
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {completedTrades.map(t => (
                <div
                  key={t.id}
                  className="p-3 rounded-lg border border-border bg-muted/20 space-y-1"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium">
                      {t.proposer_team_name} ↔ {t.receiver_team_name}
                    </span>
                    <span className="text-[10px] text-muted-foreground">
                      {new Date(t.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="text-[10px] text-muted-foreground">
                    Players: {t.player_names.join(', ')}
                  </p>
                  {confirmReverse === t.id ? (
                    <div className="flex gap-2 pt-1">
                      <Button
                        size="sm"
                        variant="destructive"
                        className="flex-1 h-7 text-xs"
                        onClick={() => handleReverseTrade(t.id)}
                        disabled={processing}
                      >
                        {processing ? 'Reversing...' : 'Confirm Reverse'}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1 h-7 text-xs"
                        onClick={() => setConfirmReverse(null)}
                      >
                        Cancel
                      </Button>
                    </div>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      className="w-full h-7 text-xs gap-1"
                      onClick={() => setConfirmReverse(t.id)}
                    >
                      <Undo2 className="w-3 h-3" />
                      Reverse This Trade
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}

          {!tradesLoading && completedTrades.length === 0 && (
            <p className="text-xs text-muted-foreground text-center py-2">No completed trades to reverse</p>
          )}
        </CardContent>
      </Card>

      {/* 4. ADJUST SCORE */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-primary" />
            Adjust Matchup Score
          </CardTitle>
          <CardDescription className="text-xs">Manually override a matchup's scores.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Button
            variant="outline"
            size="sm"
            onClick={loadMatchups}
            disabled={matchupsLoading}
            className="w-full"
          >
            {matchupsLoading ? 'Loading...' : 'Load Matchups'}
          </Button>

          {matchups.length > 0 && !selectedMatchup && (
            <div className="max-h-48 overflow-y-auto space-y-1">
              {matchups.map(m => (
                <button
                  key={m.id}
                  onClick={() => {
                    setSelectedMatchup(m);
                    setNewHomeScore(m.home_score.toString());
                    setNewAwayScore(m.away_score.toString());
                  }}
                  className="w-full text-left px-3 py-2 rounded text-sm hover:bg-muted/50 border border-border flex items-center justify-between"
                >
                  <span>Wk {m.week}: {m.home_team} vs {m.away_team}</span>
                  <span className="text-xs text-muted-foreground">{m.home_score} - {m.away_score}</span>
                </button>
              ))}
            </div>
          )}

          {selectedMatchup && (
            <div className="space-y-3 p-3 rounded-lg border border-border bg-muted/20">
              <p className="text-xs font-medium">
                Week {selectedMatchup.week}: {selectedMatchup.home_team} vs {selectedMatchup.away_team}
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">{selectedMatchup.home_team}</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={newHomeScore}
                    onChange={(e) => setNewHomeScore(e.target.value)}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">{selectedMatchup.away_team}</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={newAwayScore}
                    onChange={(e) => setNewAwayScore(e.target.value)}
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={handleAdjustScore}
                  disabled={processing}
                  className="flex-1"
                >
                  {processing ? 'Saving...' : 'Save Score'}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setSelectedMatchup(null)}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* 5. SET LINEUP */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Users className="w-4 h-4 text-primary" />
            Set Lineup (Inactive Owner)
          </CardTitle>
          <CardDescription className="text-xs">Swap starter/bench slots for any team.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1">
            <Label className="text-xs">Team</Label>
            <Select
              value={lineupTeamId}
              onValueChange={(v) => { setLineupTeamId(v); setLineupSwapSource(null); loadLineupRoster(v); }}
            >
              <SelectTrigger><SelectValue placeholder="Select team..." /></SelectTrigger>
              <SelectContent>
                {teamOptions.map(t => (
                  <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {lineupSwapSource && (
            <Card className="bg-primary/10 border-primary/30">
              <CardContent className="p-2 flex items-center justify-between">
                <span className="text-xs font-medium text-primary">
                  Moving: {lineupSwapSource.full_name}. Tap target to swap
                </span>
                <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={() => setLineupSwapSource(null)}>Cancel</Button>
              </CardContent>
            </Card>
          )}

          {lineupLoading ? (
            <Skeleton className="h-32" />
          ) : lineupRoster.length > 0 ? (
            <div className="max-h-64 overflow-y-auto space-y-1">
              {lineupRoster.map(p => (
                <button
                  key={p.player_id}
                  onClick={() => {
                    if (!lineupSwapSource) {
                      setLineupSwapSource(p);
                    } else if (lineupSwapSource.player_id !== p.player_id) {
                      handleSetLineup(lineupSwapSource, p);
                    } else {
                      setLineupSwapSource(null);
                    }
                  }}
                  className={cn(
                    "w-full text-left px-3 py-2 rounded text-sm hover:bg-muted/50 border border-border flex items-center justify-between",
                    lineupSwapSource?.player_id === p.player_id && "border-primary bg-primary/10",
                    lineupSwapSource && lineupSwapSource.player_id !== p.player_id && "hover:border-primary/50"
                  )}
                >
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-[10px] w-10 justify-center font-mono">
                      {p.slot === 'BENCH' ? 'BN' : p.slot}
                    </Badge>
                    <span>{p.full_name}</span>
                  </div>
                  <Badge variant={(p as any).is_starter ? 'default' : 'secondary'} className="text-[10px]">
                    {(p as any).is_starter ? 'Starter' : 'Bench'}
                  </Badge>
                </button>
              ))}
            </div>
          ) : lineupTeamId ? (
            <p className="text-xs text-muted-foreground text-center py-2">No players on roster</p>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
};

export default CommissionerOverrides;
